// Building tiles (vertex-colored GLB from swissBUILDINGS3D), distance-culled.
//
// Lighting: the bake folds the hemisphere + sun term into COLOR_0
// (pipeline/buildings.py bake_vertex_light, meta.buildings.vertex_light), so
// these draw with MeshBasicMaterial — no runtime lighting at all, the same
// deal the terrain gets from its pre-lit ortho. The geometry is flat-shaded,
// so the baked per-vertex term equals a per-fragment Lambert. The bake uses
// the WORLD's sun (region.lighting), an exposure and outward-oriented walls;
// main.js aims the scene lights from the same spec (scenelight.js sceneLight).
import * as THREE from 'three';
import { attachCut, cutBoxes, cutGen } from './worldcut.js';
import { cellSort, permuteTris, unpermuteTris, cutMeets, cutPieces } from './cellruns.js';
import { run } from './pool.js';
import { act, heavyOk } from './sched.js';
import RENDER_CFG from './renderconfig.json';
import { fetchAsset, listed, retryable } from './assetq.js';
import { decode as decodeCol } from '../../game/colbin.js';
import { glbFocusWant } from './farplan.js';
import { DRAW_ORDER } from './visframe.js';
import { classDraw } from './device.js';
import { vrsCap } from './vrstyle/index.js';
import { facadeFile, texAniso } from './texq.js';
import { drawK, onDraw } from './adaptres.js';
import { SWAP_U, swapBefore } from './farbld.js';

// Facade atlas sampling (meta.buildings.facades): walls carry a continuous bay/row
// uv (TEXCOORD_0) + their atlas tile origin (TEXCOORD_1); the shader wraps
// the bay uv with fract() inside the tile and samples with textureGrad so
// mip selection follows the CONTINUOUS uv (a plain fract would smear a full
// mip chain seam across every storey boundary). COLOR_0 on those walls is
// lighting-only — texel x vertex color reproduces the prelit math.
// Roofs/doors/fascia/galleries point at the atlas's white tile, so one
// material draws everything and the draw-call budget is unchanged.
//
// `tile` (= 1/GRID) is DATA, read from the bake's meta — the atlas grid went
// 8 -> 12 with facade_atlas rev 6 (3072 px, 144 slots) and nothing here moved
// but the literal's precision: 1/12 is not exact, so the constant folded into
// the shader is printed to 8 places. It is deliberately rounded DOWN by the
// toFixed — fract(uv) * tile then reaches 255.99997 px of a 256 px tile and
// can never bleed into the neighbour.
// TRIM BIAS (facade-thin, APP_FACADES "Trim depth bias"): the white-tile
// VERTICALS — fascia boards (gen_fascia: hung 0.40 m from the roof edge, IN
// the wall plane wherever the roof has no overhang) and the door layers
// (5–7.6 cm off the wall, 2–8 mm apart) — sit closer to the surface behind
// them than the GLBQ position quantum (1/64 m) and the 24-bit depth step
// (z² / (near · 2^24): 3 cm at 200 m, 12 cm at 400 m) can separate, so they
// z-fight: a board blinks over its full width, a door through its cell. The
// vertex moves along its own eye ray (the same pixel in every eye, only its
// depth changes) by TRIM_M over the view's cosine to the face (a slope term:
// a grazing view needs more ray length per cm of offset) plus TRIM_STEPS
// depth steps at its view depth (wins the tie at any range), capped at
// TRIM_CAP of its distance. A portable polygon offset for this layer only:
// one material keeps the draw. Walls, roofs and every other tile untouched.
const TRIM_M = '0.025', TRIM_STEPS = '3.0', TRIM_CAP = '0.004';
const TRIM_GLSL = `
  if (projectionMatrix[2][3] < -0.5 && abs(normal.y) < 0.3
      && all(lessThan(abs(uv1 - uTrimWhite), vec2(2e-3)))) {
    float tDl = length(mvPosition.xyz), tDz = max(-mvPosition.z, 1e-3);
    vec3 tN = normalize(mat3(modelViewMatrix) * normal);
    float tCos = max(abs(dot(tN, mvPosition.xyz)) / tDl, 0.25);
    float tNear = projectionMatrix[3][2] / (projectionMatrix[2][2] - 1.0);
    float tS = ${TRIM_M} / tCos + ${TRIM_STEPS} * tDz * tDl / (tNear * 16777216.0);
    mvPosition.xyz *= 1.0 - min(tS, ${TRIM_CAP} * tDl) / tDl;
    gl_Position = projectionMatrix * mvPosition;
  }`;

// FAR-SWAP + TRIM DROP (far-bld-model w41, APP_FACADES §11; bldswap.js the
// rules, farbld.js swapBefore the per-pass θ in uSwap): after the trim bias
//   - a white-tile vertical whose uv carries its centroid + altitude
//     (trimEncode: uv.y > 30000) drops WHOLE when alt · uSwap.z < d − 1.5
//     (its altitude projects under TRIM_PX px; far = small);
//   - a triangle of a far-shape cluster (aSwap.z = e > 0) collapses past its
//     switch distance S = e · uSwap.x + R, and its facade texel fades to the
//     tile mean over [uSwap.w · S, S] (vSwapF): the far shape (farbld.js)
//     shows from S − BAND_M on. A collapse = one clip-outside point on all
//     three vertices: the triangle is culled whole, nothing rasterised.
const SWAP_GLSL = `
  vSwapF = 0.0;
  if (uSwap.y > 0.5) {
    if (uSwap.z > 0.0 && uv.y > 30000.0 && abs(normal.y) < 0.3
        && all(lessThan(abs(uv1 - uTrimWhite), vec2(2e-3)))) {
      float tv = floor(uv.x), th = floor(tv / 256.0), ta = tv - th * 256.0;
      vec2 tc = vec2(th, floor(uv.y) - 32768.0) * 2.0 + 1.0;
      if (ta < 255.0 && ta * 0.04 * uSwap.z < distance(cameraPosition.xz, tc) - 1.5) gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    }
    if (uSwap.x > 0.0 && aSwap.z > 0.0) {
      float sS = aSwap.z * 0.01 * uSwap.x + aSwap.w;
      float sD = distance(cameraPosition.xz, aSwap.xy);
      if (sD > sS) gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
      vSwapF = smoothstep(uSwap.w * sS, sS, sD);
    }
  }`;

// FACADE PREFILTER (quest-shimmer w53, APP_FACADES §13): the bay uv wraps
// INSIDE an atlas tile that has no padding, so the filter footprint of a
// sample near a storey / bay seam (bilinear half texel at the picked mip +
// the anisotropic line along the major axis) reaches into the NEIGHBOUR tile
// (another class's palette, the white tile): a thin foreign line at every
// seam, periodic over the wall — moiré that crawls under sub-pixel motion.
// (1) CLAMP: the wrapped point is held half its footprint's uv box + half
// the sampled mip's texel (max(minor, major × FAC_AA.w), FAC_AA.w = 1 /
// the sampler's anisotropy; box extents, no sqrt) inside its own tile.
// (2) FADE: the clamp alone biases a footprint near the tile size toward
// the tile's centre (the window), so the texel fades to the tile mean (the
// FAR-SWAP fade's mip-7 tap) over [FAC_AA.y, FAC_AA.z] tile units a pixel
// of the box's larger extent (a bay of 4 → 2 px); the tap only where the
// fade is > 0. FAC_AA.x = on (0 = the look before w53: the seam lab's
// `facaa0` arm, `__vr.facaa`). One material, no draw, the program key kept.
export const FAC_AA = { value: new THREE.Vector4(1, 0.25, 0.5, 1 / 8) };

function facadePatch(mat, tile, trimWhite) {
  const prev = mat.onBeforeCompile;           // chain AFTER the worldcut patch
  mat.onBeforeCompile = (shader) => {
    if (prev) prev(shader);
    shader.uniforms.uTrimWhite = trimWhite;
    shader.uniforms.uSwap = SWAP_U;
    shader.uniforms.uFacAA = FAC_AA;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>',
        '#include <common>\nattribute vec2 uv1;\nattribute vec4 aSwap;\nuniform vec2 uTrimWhite;\nuniform vec4 uSwap;\nvarying vec2 vFUv;\nvarying vec2 vFTile;\nvarying float vSwapF;')
      .replace('#include <begin_vertex>',
        '#include <begin_vertex>\nvFUv = uv;\nvFTile = uv1;')
      .replace('#include <project_vertex>', '#include <project_vertex>' + TRIM_GLSL + SWAP_GLSL);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>',
        '#include <common>\nuniform vec4 uFacAA;\nvarying vec2 vFUv;\nvarying vec2 vFTile;\nvarying float vSwapF;')
      .replace('#include <map_fragment>',
        `{
          vec2 fGx = dFdx(vFUv), fGy = dFdy(vFUv), fF = fract(vFUv);
          float fMix = vSwapF;
          if (uFacAA.x > 0.5) {
            vec2 fE = abs(fGx) + abs(fGy);               // the pixel footprint's box, tile units
            float fMx = max(fE.x, fE.y);
            float fT = max(max(min(fE.x, fE.y), fMx * uFacAA.w), ${(1/256).toFixed(8)});   // the sampled mip's texel
            vec2 fM = min(0.5 * (fE + fT), vec2(0.5));
            fF = clamp(fF, fM, 1.0 - fM);
            if (uFacAA.z > uFacAA.y) fMix = max(fMix, smoothstep(uFacAA.y, uFacAA.z, fMx));
          }
          vec2 fWrap = vFTile + fF * ${tile};
          vec4 sampledDiffuseColor = textureGrad(map, fWrap,
            fGx * ${tile}, fGy * ${tile});
          // the tile mean (FAR-SWAP fade, the prefilter's fade) = the box
          // mip 7 (2 × 2 texels a 256 px tile) sampled at the tile's centre
          if (fMix > 0.0) sampledDiffuseColor = mix(sampledDiffuseColor,
            textureLod(map, vFTile + vec2(0.5 * ${tile}), 7.0), fMix);
          diffuseColor *= sampledDiffuseColor;
        }`);
  };
  mat.customProgramCacheKey = () => 'worldcut-facade' + tile + (mat.userData.inside ? '-in' : '') + (mat.userData.nocut ? '-nc' : '');
}

// Back side = INSIDE. Roofs carry UP normals
// (orient_up), walls OUTWARD ones (flip_part + the per-triangle
// orient_walls_out), fascia boards face away from their roof — so a fragment
// seen from behind its normal is a roof soffit, the inner face of a board or
// an interior wall (an opened station hall, buildings.py _station_open).
// COLOR_0 bakes the OUTSIDE's sun, which lit an overhang from below like a
// sunlit top. The normal decides, not gl_FrontFacing: fascia quads are wound
// by edge order. One compare per fragment, no geometry — the materials were
// already DoubleSide, so the back faces were always being drawn.
const INSIDE_SHADE = '0.32';
function insidePatch(mat) {
  const prev = mat.onBeforeCompile;           // chain AFTER worldcut (vCutW)
  mat.onBeforeCompile = (shader) => {
    if (prev) prev(shader);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vInN;')
      .replace('#include <begin_vertex>',
        '#include <begin_vertex>\nvInN = mat3(modelMatrix) * normal;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vInN;')
      .replace('#include <color_fragment>', '#include <color_fragment>\n'
        + `if (dot(vInN, cameraPosition - vCutW) < 0.0) diffuseColor.rgb *= ${INSIDE_SHADE};`);
  };
}

// a GLB mesh in cell order (cellruns.js): its world-coordinate soup permuted
// per triangle, the cell table on the geometry (cells need world
// coordinates: a mesh under a moved node keeps its whole draw). A soup the
// pool read (glb.soup, pool-glb.js) comes permuted already: its order and
// cells (userData.soup) are taken here, the permutation undone where the
// mesh keeps its whole draw
function cellOrder(o) {
  const g = o.geometry, A = g.attributes, p = A.position, pre = g.userData.soup;
  if (pre) delete g.userData.soup;
  let whole = !!g.index || !o.matrixWorld.equals(_ID);
  for (const k in A) if (A[k].isInterleavedBufferAttribute) whole = true;
  if (whole) {
    if (pre && pre.order) for (const k in A) unpermuteTris(pre.order, A[k].array, A[k].itemSize);
    return;
  }
  const cs = pre ? pre.order && { order: pre.order, ...pre.cells } : cellSort(p.array, null);
  if (!cs) return;
  for (const k in A) { if (!pre) permuteTris(cs.order, A[k].array, A[k].itemSize); A[k].needsUpdate = true; }
  g.userData.cells = { cells: cs.cells, blocks: cs.blocks };
}
const _ID = new THREE.Matrix4(), _V = new THREE.Vector3();

// A GLBQ tile (game/glbsoup.js: the one reader) as the scene GLTFLoader used
// to hand over: a Group holding one Mesh, identity matrices, the world-space
// non-indexed f32 soup under GLTFLoader's attribute names (cellOrder permutes
// it in place). The material is a placeholder: _install sets the real one.
const _SOUP_MAT = new THREE.MeshBasicMaterial();
function soupScene(s) {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(s.position, 3));
  g.setAttribute('normal', new THREE.BufferAttribute(s.normal, 3));
  g.setAttribute('color', new THREE.BufferAttribute(s.color, 3));
  if (s.uv0) {
    g.setAttribute('uv', new THREE.BufferAttribute(s.uv0, 2));
    g.setAttribute('uv1', new THREE.BufferAttribute(s.uv1, 2));
  }
  // FAR-SWAP (bldswap.js): the pool's per-vertex cluster, else 0 = never swapped
  const sw = s.swap && s.swap.aSwap && s.swap.aSwap.length === s.position.length / 3 * 4 ? s.swap.aSwap : null;
  g.setAttribute('aSwap', new THREE.BufferAttribute(sw || new Int16Array(s.position.length / 3 * 4), 4));
  if (s.order !== undefined) g.userData.soup = { order: s.order, cells: s.cells };   // the pool's cell order (cellOrder)
  const root = new THREE.Group();
  root.add(new THREE.Mesh(g, _SOUP_MAT));
  return root;
}
export class BuildingManager {
  constructor(scene, meta, baseUrl, collider, ktx2) {
    this.scene = scene;
    this.meta = meta;
    this.baseUrl = baseUrl;
    this.collider = collider || null;
    this.group = new THREE.Group();
    this.group.name = 'buildings';
    this.group.matrixAutoUpdate = false;   // static: a tile's matrices are set once (_install)
    scene.add(this.group);
    this.tiles = new Map();
    this._drawnGen = 0;           // bumps on every install / evict (drawnKeys)
    this._held = new Map();       // program key -> a retired material (_retire)
    this.colLoaded = new Map();   // collision requested (in flight or added) -> its request token
    this._colIn = new Set();      // collision tiles added, or failed once (nearColReady)
    // ... × the class's draw rule (device.js CLASS_DRAW bld_cull, quest-budget):
    // the ring's RESIDENCY radius. The draw rung (adaptres.js drawK) narrows
    // only what is DRAWN of it (the ring pass below: a resident tile beyond
    // cull × k is hidden, never evicted — no GLB thrash while the ladder moves)
    this.cull = vrsCap(RENDER_CFG.buildings_cull_m * classDraw('bld_cull'));   // THE VR LOOK: ≤ the void (vrstyle/)
    onDraw(() => { this._replan = true; });
    // Ring loads are QUEUED, nearest first (renderconfig `load`): every tile
    // in the cull used to start its GLB and its collision json at once, in
    // index order — ~25 tiles, ~60 MB, ahead of the terrain under the player.
    // Collision runs col_in_flight at a time; GLBs glb_in_flight at a time,
    // and only within building_near_m until main.js calls allowFar() (the
    // beam-in latch). Both queues are rebuilt on every ring pass, so a jump drops
    // the old spot's unstarted loads. Focus (wedge) loads bypass them.
    const lc = RENDER_CFG.load || {};
    this._nearM = lc.building_near_m ?? 700;
    this._glbMax = lc.glb_in_flight ?? 2;
    this._colMax = lc.col_in_flight ?? 4;
    this._far = false;
    this._glbQ = [];              // [{ t, key, d }] by d
    this._colQ = [];              // [{ key, tx, ty, d }] by d
    this._glbN = 0;
    this._colN = 0;
    this._stale = new Set();      // GLB keys in flight a world swap invalidated (invalidateTiles)
    this._replan = false;         // the next update() runs a ring pass (invalidateTiles)
    this._dead = new Set();       // GLB keys that failed deterministically (_load), never asked again until invalidateTiles
    this.dropN = 0;               // GLB tiles dropped DEAD (load drop.buildings)
    // DRIP FEED (freeze-free entry): after the latch the far GLBs (5–12 MB)
    // stream in, and each one's parse + build and first-draw uploads used to
    // land in whatever task finished its fetch, two at a time, next to the
    // facade atlas's decode and ~36 MB upload — 3.5 s 'glb.build' tasks.
    // Now (_drip, once a frame from update): at most ONE parse starts per
    // frame, in its own task (act 'glb.parse'; `_parseQ`, one in flight); a
    // built tile joins the graph HIDDEN, `prepare(root)` (main.js: its
    // programs compiled sliced) runs, then at most one tile a frame is shown
    // (its buffers upload in that frame alone); the atlas is transcoded off
    // the main thread (KTX2Loader's workers) and uploaded in its own task
    // once heavyOk (`upload(tex)`, main.js: renderer.initTexture).
    this._parseQ = [];            // [{ key, run }] fetched GLBs waiting for their parse
    this._parsing = null;         // the key whose parse runs now
    this._reveal = [];            // [{ key, root, ready }] built, hidden until shown
    this.prepare = null;          // (root) => Promise: compile before the first draw (main.js)
    this.far = null;              // FarBuildings (main.js): the far tiles the pool measures against (FAR-SWAP)
    this.upload = null;           // (texture) => void: one upload, its own task (main.js)
    this.info = (meta.buildings && meta.buildings.tiles) || [];
    this.tileM = (meta.buildings && meta.buildings.tile_m) || 1024;
    this.lastUpdate = new THREE.Vector3(1e9, 0, 0);
    // Scope focus (client/src/focus.js): the wedge the optic looks at streams
    // tiles PAST the ring cull onto `focus.layer`, which only the monocular /
    // scope cameras render — the naked-eye pass is byte-identical to before.
    // The want-set is bounded by triangles, never by a tile count (tiles run
    // 80 … 62 k tris on the live bake, so a count cap bounds nothing).
    this.focusWant = new Set();   // keys the wedge wants, ring keys already removed
    this.focusLayer = 0;
    this.focusGen = -1;           // focus.gen the last focus pass ran for
    this.focusOn = false;         // focus.active at that pass
    // facade atlas — ONE texture shared by every tile material.
    // A placeholder is assigned synchronously: a GLB tile that loads before
    // the atlas must still get the facade material, or it would keep its
    // lighting-only COLOR_0 and render gray forever; _drip swaps the KTX2
    // texture into every material once it is transcoded.
    this.facades = (meta.buildings && meta.buildings.facades) || null;
    // The bytes come through the asset queue as `bulk` (t43): location-
    // independent, requested at boot, so the Map's idle time fetches them.
    // The file is a KTX2 (pipeline/atlasktx2.mjs): texq.js facadeFile picks
    // the UASTC twin `file_hq` under TEX_HQ (ASTC 4x4 on phone / Quest, BC7
    // on desktop), else the ETC1S `file` (ETC2 RGB / BC7), transcoded by
    // main.js's ONE KTX2Loader in its workers straight to a compressed
    // texture (exact data never passes a canvas); the anisotropy is texq's
    // `facade` (8 with the switch off). The bake stored the rows
    // bottom-first, as three's flipY of the old image had them. A non-ok
    // answer leaves the placeholder empty.
    this.ktx2 = ktx2 || null;
    this.facadeTex = null;
    this._atlasTex = null;        // the transcoded atlas waiting for its upload task
    if (this.facades) {
      const tex = this.facadeTex = new THREE.Texture();
      tex.colorSpace = THREE.SRGBColorSpace;
      if (this.ktx2) {
        fetchAsset(`${baseUrl}/${facadeFile(this.facades)}`, { prio: 'bulk', as: 'arrayBuffer' }).then((ab) => {
          if (!ab) return;
          this.ktx2.parse(ab, (t) => { t.anisotropy = texAniso('facade', 8); FAC_AA.value.w = 1 / Math.max(1, t.anisotropy); this._atlasTex = t; },
            (e) => console.warn('[buildings] atlas ktx2:', e && e.message));
        }).catch(() => {});
      }
    }
    // the facade KEEPERS (farbld.js's idiom): hidden, empty meshes wearing
    // the facade material from boot — with and without the cut loop
    // (CUT-SPLIT, _cutSplit) —, so every warm-up pass — the canvas job
    // and the optic passes (main.js warmSet) — builds the 'worldcut-facade…'
    // programs even with no GLB tile resident yet. A tile's prepare() makes
    // its canvas program only: a tile landing after the warm-up built the
    // optic copy inside its first optic frame. Never drawn, never disposed
    // (it holds the programs like _held); the atlas swap (_drip) dresses it too.
    // the atlas's WHITE tile origin (uv1) for the trim bias: not in the served
    // meta, so read off the first tile's roofs (_whiteOf); shared by every
    // facade program as one uniform (the keepers' programs too)
    this._trimWhite = { value: new THREE.Vector2(-9, -9) };
    this.keeper = this.keeperNc = null;
    if (this.facadeTex) {
      const kg = new THREE.BufferGeometry();
      for (const [k, n] of [['position', 3], ['color', 3], ['uv', 2], ['uv1', 2]]) kg.setAttribute(k, new THREE.BufferAttribute(new Float32Array(0), n));
      kg.setAttribute('aSwap', new THREE.BufferAttribute(new Int16Array(0), 4));
      const keep = (cut) => {
        const o = new THREE.Mesh(kg, this._material(kg, cut));
        o.name = 'buildings-keeper' + (cut ? '' : '-nc');
        o.visible = false;
        o.frustumCulled = false;
        o.matrixAutoUpdate = false;
        return o;
      };
      this.keeper = keep(true);
      this.keeperNc = keep(false);
      this.group.add(this.keeper, this.keeperNc);
    }
    this._cutGen = cutGen();
  }

  // cut false: without the station cut's discard loop (CUT-SPLIT, _cutSplit)
  _material(geom, cut = true) {
    // facade material only when this geometry really carries the uv channels
    // (a pre-67 GLB cached by the browser must never meet the atlas shader)
    if (this.facadeTex && geom.getAttribute('uv') && geom.getAttribute('uv1')) {
      const m = new THREE.MeshBasicMaterial({
        map: this.facadeTex, vertexColors: true, side: THREE.DoubleSide, fog: true,
      });
      attachCut(m, cut);
      this._inside(m);
      facadePatch(m, (this.facades.tile || 0.125).toFixed(8), this._trimWhite);
      m.onBeforeRender = swapBefore;   // this pass's θ into uSwap (farbld.js)
      return m;
    }
    const m = new THREE.MeshBasicMaterial({
      vertexColors: true, side: THREE.DoubleSide, fog: true,
    });
    attachCut(m, cut);
    this._inside(m);
    return m;
  }

  // the inside shade rides the facade atlas's wall orientation (walls wound
  // OUTWARD): a world without the atlas draws no inside shade
  _inside(m) {
    if (!this.facades) return;
    insidePatch(m);
    m.userData.inside = true;
    m.customProgramCacheKey = () => 'worldcut-in' + (m.userData.nocut ? '-nc' : '');   // facadePatch re-keys its own
  }

  // three frees a program with the last material using it: a walk out of
  // town (every building tile evicted) threw the building programs away —
  // the canvas one and the optic-RT copy the briefing warm-up compiled
  // (main.js warmShaders) — and the next town rebuilt them mid-game. One
  // evicted material per program key is held instead of disposed: it keeps
  // its programs alive and draws nothing (its geometry is gone, its map is
  // the shared atlas).
  _retire(m) {
    const k = m.customProgramCacheKey();
    if (this._held.has(k)) m.dispose();
    else this._held.set(k, m);
  }

  // A non-2xx used to resolve to null and KEEP the key, so a tile that 404'd or
  // 5xx'd once was never retried — fully rendered, permanently non-colliding.
  // Only a successful addTile may mark the key as loaded — except a file the
  // world manifest does not list (a bake from before COL1): no file, never
  // asked, never retried (the key stays until _uncol, so a world swap asks the
  // new manifest), settled for nearColReady.
  _loadCollision(key, tx, ty) {
    if (!this.collider || this.colLoaded.has(key)) return;
    const url = `${this.baseUrl}/buildings/col_${tx}_${ty}.bin`;
    if (!listed(url)) { this.colLoaded.set(key, null); this._colIn.add(key); return; }
    const tok = {};
    this.colLoaded.set(key, tok);
    this._colN++;
    // class `near`: the ground at the target (assetq.js); COL1 bytes
    // (game/colbin.js, the decoder the server shares) -> the JSON shape
    fetchAsset(url, { prio: 'near', as: 'arrayBuffer' })
      .then((data) => {
        if (data === null) throw new Error('col: HTTP error');
        // the GLB was discarded (or the tile evicted, or a world swap
        // invalidated it) while this was in flight: that dropped the key (a
        // new request holds another token), and a late add would never leave
        if (this.colLoaded.get(key) !== tok) return;
        this.collider.addTile(key, decodeCol(data));
        this._colIn.add(key);
      })
      // failed: retried on a later ring pass; settled for nearColReady, so a
      // missing file cannot hold the beam-in overlay up for good
      .catch(() => { if (this.colLoaded.get(key) === tok) this.colLoaded.delete(key); this._colIn.add(key); })
      .finally(() => { this._colN--; this._pump(); });
  }

  // Start queued ring loads while there is room: collision first, then GLBs
  // (near ones only until allowFar). Entries are checked again here — the
  // tile may have loaded through another path or left the cull since.
  _pump() {
    while (this._colN < this._colMax && this._colQ.length) {
      const c = this._colQ.shift();
      if (!this.colLoaded.has(c.key)) this._loadCollision(c.key, c.tx, c.ty);
    }
    while (this._glbN < this._glbMax && this._glbQ.length) {
      const g = this._glbQ[0];
      if (!this._far && g.d > this._nearM) break;   // sorted: nothing nearer waits
      this._glbQ.shift();
      if (this.tiles.has(g.key)) continue;
      this._glbN++;
      this._load(g.t, g.key, () => { this._glbN--; this._pump(); });
    }
  }

  // once a frame (update): start one queued parse in its own task, show one
  // prepared tile, hand the decoded atlas to its upload task (see the ctor)
  _drip() {
    if (this._atlasTex && heavyOk()) {
      const tex = this._atlasTex, old = this.facadeTex;
      this._atlasTex = null;
      setTimeout(() => act('glb.atlas', () => {
        // the same program (a map either way): only the bound texture moves
        const swap = (m) => { if (m && m.map === old) m.map = tex; };
        this.group.traverse((o) => { if (o.isMesh) swap(o.material); });
        for (const m of this._held.values()) swap(m);
        this.facadeTex = tex;
        old.dispose();
        if (this.upload) this.upload(tex);
      }), 0);
    }
    if (!this._parsing && this._parseQ.length && !(this.far && this.far.swapPending())) {
      const job = this._parseQ.shift();
      this._parsing = job.key;
      setTimeout(() => act('glb.parse', job.run), 0);
    }
    for (let i = 0; i < this._reveal.length; i++) {
      const r = this._reveal[i];
      if (this.tiles.get(r.key) !== r.root) { this._reveal.splice(i--, 1); continue; }   // evicted meanwhile
      if (!r.ready) continue;
      this._reveal.splice(i, 1);
      r.root.visible = !r.root.userData.culled;
      this._drawnGen++;
      break;
    }
  }

  // main.js beam-in latch: the GLBs past building_near_m may load now.
  allowFar() {
    if (this._far) return;
    this._far = true;
    this._pump();
  }

  // Collision of every tile the player can be standing in (edge distance
  // <= 0 from the last ring pass) has been added, or failed once. Part of
  // the beam-in latch: a body must not walk through a house in the first
  // seconds. True without buildings (or before the first pass: the latch
  // also needs terrain.nearReady, which needs an update).
  get nearColReady() {
    if (!this.collider || !this.info.length || this.lastUpdate.x > 1e8) return true;
    const half = this.tileM * 0.71;
    for (const t of this.info) {
      const [tx, ty] = t.tile;
      const d = Math.hypot((tx + 0.5) * this.tileM - this.lastUpdate.x, (ty + 0.5) * this.tileM - this.lastUpdate.z) - half;
      if (d <= 0 && !this._colIn.has(tx + '_' + ty)) return false;
    }
    return true;
  }

  // the GLB tiles within r of pos (default the ring's cull: ≤ the void's D under the VR look) not drawn yet — absent,
  // loading or awaiting their reveal; dead ones aside — the VR landing's settle (main.js vrSettle)
  pendingNear(pos, r = this.cull) {
    let n = 0;
    const half = this.tileM * 0.71;
    for (const t of this.info) {
      const [tx, ty] = t.tile, key = tx + '_' + ty;
      if (Math.hypot((tx + 0.5) * this.tileM - pos.x, (ty + 0.5) * this.tileM - pos.z) - half >= r || this._dead.has(key)) continue;
      const v = this.tiles.get(key);
      if (!v || v === 'loading' || this._reveal.some((q) => q.root === v)) n++;
    }
    return n;
  }

  // The GLB tiles DRAWN now (installed, either layer), as 'tx_ty' keys —
  // the far-building LOD hides exactly these (farbld.js). `drawnGen` bumps
  // whenever the set changes, so the LOD re-plans only then.
  get drawnGen() { return this._drawnGen; }
  drawnKeys() {
    const out = new Set();
    for (const [k, v] of this.tiles) if (v !== 'loading' && v.visible) out.add(k);
    return out;
  }

  // FAR-SWAP: the ring tiles DRAWN now on layer 0 (farbld.js draws their far
  // shapes in the main view, each past its own switch distance)
  ringKeys() {
    const out = new Set();
    for (const [k, v] of this.tiles) if (v !== 'loading' && v.visible && !v.userData.focus) out.add(k);
    return out;
  }

  // Focus-only tiles sit on the optic layer, ring tiles on layer 0. The flag
  // rides with the mesh so a late load and a ring↔wedge flip agree on it.
  _setFocus(root, on) {
    const layer = on ? this.focusLayer : 0;
    root.traverse((o) => o.layers.set(layer));
    root.userData.focus = on;
    // FAR-SWAP: a focus-only tile never swaps (its far shapes draw on the
    // focus layer by farPlan, never the main mesh): its aSwap reads 0 meanwhile
    root.traverse((o) => {
      const a = o.isMesh && o.geometry.getAttribute('aSwap');
      if (!a) return;
      const U = o.geometry.userData;
      if (!U.swapArr) U.swapArr = a.array;
      const want = on ? (U.swapZero || (U.swapZero = new Int16Array(a.array.length))) : U.swapArr;
      if (a.array !== want) { a.array = want; a.needsUpdate = true; }
    });
  }

  // A parsed tile joins the group, hidden until prepared and its frame comes
  // (_drip). Static: every node's matrix is composed once here, its world
  // matrix set on joining, and three never recomposes it per frame.
  _install(root, focusOnly) {
    root.traverse((o) => {
      if (o.isMesh) {
        o.frustumCulled = true;
        // first among the opaques (APP_VISIBILITY §3d, P1): the cheapest
        // shader and the largest near occluder — early-z then kills the
        // ground / sprite / structure fragments a facade covers
        o.renderOrder = DRAW_ORDER.buildings;
        // here, not in the first frame's frustum test
        if (!o.geometry.boundingSphere) o.geometry.computeBoundingSphere();
      }
      o.matrixAutoUpdate = false;
      o.updateMatrix();
    });
    this._setFocus(root, focusOnly);
    root.visible = false;
    this.group.add(root);
    root.updateMatrixWorld(true);
    // P3 cell runs (APP_VISIBILITY §3e): each mesh's triangles in 128 m
    // cell order (the bake writes world-coordinate, non-indexed soups: every
    // attribute permuted per triangle, the draw path unchanged); viscull.js
    // draws the visible runs
    root.traverse((o) => { if (o.isMesh) cellOrder(o); });
    if (this._trimWhite.value.x < -1) this._whiteOf(root);
    this._cutSplit(root);
  }

  // the white tile = the uv1 most roof vertices (normal up) carry: roofs,
  // doors, fascia and galleries all point at it (APP_FACADES §4), the facade
  // walls at their own tiles. A sample of ≤ 4096 vertices per mesh; fewer
  // than 16 roof vertices decide nothing (the next tile asks again)
  _whiteOf(root) {
    const cnt = new Map();
    root.traverse((o) => {
      if (!o.isMesh) return;
      const n = o.geometry.getAttribute('normal'), u = o.geometry.getAttribute('uv1');
      if (!n || !u) return;
      const st = Math.max(1, Math.floor(n.count / 4096));
      for (let i = 0; i < n.count; i += st) {
        if (n.getY(i) < 0.7) continue;
        const k = Math.round(u.getX(i) * 4096) * 8192 + Math.round(u.getY(i) * 4096);
        cnt.set(k, (cnt.get(k) || 0) + 1);
      }
    });
    let best = -1, bn = 15;
    for (const [k, c] of cnt) if (c > bn) { best = k; bn = c; }
    if (best >= 0) this._trimWhite.value.set(Math.floor(best / 8192) / 4096, (best % 8192) / 4096);
  }

  // CUT-SPLIT (APP_SURFACE_PERF): the station-terminal cut boxes (worldcut.js
  // — never a wall poking into a docked funicular cabin) draw only where they
  // can cut. A mesh's cells meeting no cut box draw with the no-cut program
  // (cellruns.js cutPieces: the runs of alike cells, each a piece mesh whose
  // geometry SHARES the soup's attributes over its drawRange, its own cell
  // table for viscull.js), so early-z holds on them; a mesh without cells
  // goes by its box. Pixel-exact: a fragment outside every box is never
  // discarded. On a cut change (cutGen, update()) the tile splits again: new
  // pieces (the old ones disposed — the shared buffers upload again), the
  // soup mesh never drawn again once split (its VAO would bind the freed
  // buffers); _unmesh disposes the soups out of the tile too.
  _cutSplit(root) {
    const boxes = cutBoxes(), U = root.userData;
    U.cutGen = cutGen();
    if (!U.cutSrc) U.cutSrc = root.children.filter((o) => o.isMesh);
    for (const s of U.cutSrc) {
      const g = s.geometry, cs = g.userData.cells, S = s.userData;
      if (!cs) {
        if (!g.boundingBox) g.computeBoundingBox();
        const b = g.boundingBox.clone().applyMatrix4(s.matrixWorld);
        this._flavour(s, cutMeets(b.min.toArray(), b.max.toArray(), boxes));
        continue;
      }
      const P = cutPieces(cs, boxes);
      if (P.length === 1 && !S.pieces) { this._flavour(s, !!P[0].cut); continue; }
      if (s.parent === root) { root.remove(s); if (S.cut !== undefined) this._retire(s.material); s.material = _SOUP_MAT; S.cut = undefined; }
      for (const o of S.pieces || []) { root.remove(o); o.geometry.dispose(); this._retire(o.material); }
      S.pieces = P.map((p) => {
        const pg = new THREE.BufferGeometry();
        for (const k in g.attributes) pg.setAttribute(k, g.attributes[k]);
        pg.setDrawRange(p.start, p.count);
        pg.userData.cells = { cells: p.cells, blocks: p.blocks };
        pg.boundingBox = new THREE.Box3();
        for (const k of p.blocks) pg.boundingBox.expandByPoint(_V.fromArray(k.lo)).expandByPoint(_V.fromArray(k.hi));
        pg.boundingSphere = pg.boundingBox.getBoundingSphere(new THREE.Sphere());
        const o = new THREE.Mesh(pg, this._material(pg, !!p.cut));
        o.name = s.name;
        o.frustumCulled = true;
        o.renderOrder = s.renderOrder;
        o.matrixAutoUpdate = false;
        o.layers.mask = root.layers.mask;
        root.add(o);
        o.updateMatrixWorld();
        return o;
      });
    }
  }

  // a whole mesh's material, with or without the cut loop
  _flavour(s, cut) {
    if (s.userData.cut === cut) return;
    if (s.userData.cut !== undefined) this._retire(s.material);
    s.material = this._material(s.geometry, cut);
    s.userData.cut = cut;
  }

  // `done` (queued ring loads): called ONCE when the load has settled either
  // way (a throw inside onGltf is reported through `failed`, as
  // GLTFLoader.parse's onError did). The bytes come through the asset queue
  // as `near` (t43): the target's visual near set, like the surface 1 m
  // tiles — this class's own queue (glb_in_flight, building_near_m until
  // allowFar) still decides what goes. The parse: readGlbSoup decodes the
  // meshopt streams (MeshoptDecoder's wasm), de-quantises and expands the
  // indexed mesh to the non-indexed soup, and the soup is put in cell order
  // (cellOrder's sort) ON THE WORKER POOL (R8c4: pool.js op glb.soup,
  // pool-glb.js — the decoder imported in the pool's bundle); the glb.parse
  // task moves the bytes there, the soup's arrays come back moved. No
  // useWorkers: its worker is built from the decoder's function source by
  // name, which the minified bundle renames (the worker throws
  // `workerProcess is not defined`, the decode never resolves). Measured in
  // node on a desktop core (2026-10-25, r0b2): the live bake's largest tile
  // 4_6 (78 k tris, 1.9 MB GLBQ) 4.8 ms decode + ~7.7 ms expand + 0.1 ms
  // geometry; 5_5 (0.9 MB) 2.1 + ~4 ms. Expect a few times that on a Quest.
  _load(t, key, done = null) {
    const [tx, ty] = t.tile;
    const cx = (tx + 0.5) * this.tileM, cz = (ty + 0.5) * this.tileM;
    const settled = () => { const d = done; done = null; if (d) d(); };
    const failed = () => {
      if (this._parsing === key) this._parsing = null;
      settled();
      if (this.tiles.get(key) === 'loading') this.tiles.delete(key);
      // a world swap invalidated it in flight (its bytes are the old world's):
      // dropped, the next update() asks again
      if (this._stale.delete(key)) this._replan = true;
    };
    // a DETERMINISTIC failure — a non-retryable answer (404: the bake lacks a
    // file its meta names) or bytes that do not parse / build: one line,
    // counted once (load drop.buildings), the key DEAD — the ring never asks
    // it again until a swap invalidates its rel (invalidateTiles). A network
    // error or a retryable status assetq gave up on (it warned and counted
    // fail_n) stays `failed`: a later ring pass may ask again.
    const dead = (why) => {
      if (!this._stale.has(key)) this._drop(t.file, key, why);
      failed();
    };
    const dir = `${this.baseUrl}/buildings/`;
    this.tiles.set(key, 'loading');
    const onGltf = (gltf) => {
      act('glb.build');       // this task's long-task label (sched.js)
      if (this._parsing === key) this._parsing = null;
      settled();
      // evicted while in flight — dropped undisplayed, nothing on the GPU to
      // dispose, as the ring path always did
      if (this.tiles.get(key) !== 'loading') return;
      if (this._stale.has(key)) { failed(); return; }
      // focusWant never holds a ring-kept key, so membership alone decides.
      // A wedge tile the wedge dropped mid-flight would land on layer 0 miles
      // past the cull, i.e. in the NAKED-EYE view — discard it instead.
      // (lastUpdate is at most 64 m stale against a 2.4 km threshold.)
      const focusOnly = this.focusWant.has(key);
      if (!focusOnly
          && Math.hypot(cx - this.lastUpdate.x, cz - this.lastUpdate.z)
             - this.tileM * 0.71 >= this.cull + 200) {
        this.tiles.delete(key);
        // its collision goes too (as _evict does): it was kept for a tile
        // that is no longer coming, and the key must load afresh on return
        this._uncol(key);
        return;
      }
      const root = gltf.scene;
      this._install(root, focusOnly);
      this.tiles.set(key, root);
      const r = { key, root, ready: !this.prepare };
      this._reveal.push(r);
      if (this.prepare) {
        Promise.resolve().then(() => this.prepare(root)).catch((e) => console.warn('[buildings] prepare:', e))
          .then(() => { r.ready = true; });
      }
    };
    fetchAsset(dir + t.file, { prio: 'near', as: 'arrayBuffer', status: true }).then(({ status: st, body: ab }) => {
      if (!ab && !retryable(st)) { dead(`HTTP ${st}`); return; }
      if (!ab || this._stale.has(key)) { failed(); return; }
      // one parse per frame, in its own task (_drip)
      this._parseQ.push({ key, run: () => {
        if (this.tiles.get(key) !== 'loading') { this._parsing = null; settled(); return; }   // evicted while queued
        if (this._stale.has(key)) { failed(); return; }
        // FAR-SWAP: the far tile (its clusters) rides along; the pool measures
        // its e on this soup and encodes the trims (bldswap.js swapSoup)
        const ft = this.far ? this.far.swapTile(key) : null;
        const tw = this._trimWhite.value.x < -1 ? null : [this._trimWhite.value.x, this._trimWhite.value.y];
        const swap = { t: ft && { tri0: ft.tri0, ntris: ft.ntris, parts: ft.parts, cl: ft.cl, pc: ft.pc, roofs: ft.roofs, segs: ft.segs }, white: tw };
        run('glb.soup', { buf: ab, swap }, { transfer: [ab], prio: 'near' }).then((s) => {
          if (ft && s.swap && s.swap.cl && this.far) this.far.applyTile(key, s.swap.cl, s.swap.pc);
          onGltf({ scene: soupScene(s) });
        })
          .catch((e) => dead((e && e.message) || String(e)));
      } });
    }, failed);
  }

  // a GLB tile dropped DEAD (_load): the line, the count, the mark
  _drop(file, key, why) {
    console.warn(`[buildings] ${file}: dropped (${why})`);
    this._dead.add(key);
    this.dropN++;
  }

  _evict(key, root) {
    this._unmesh(key, root);
    // collision was never evicted with the mesh, so it grew for the whole
    // session. Safe to drop here: a tile only gets collision inside the cull
    // radius and is only evicted 200 m beyond it (or from the wedge, which
    // lives further out still), far outside any body's reach.
    this._uncol(key);
  }

  _unmesh(key, root) {
    this.group.remove(root);
    root.traverse((o) => {
      if (o.isMesh) { o.geometry.dispose(); this._retire(o.material); }
    });
    for (const s of root.userData.cutSrc || []) if (s.parent !== root) s.geometry.dispose();   // split soups (_cutSplit)
    this.tiles.delete(key);
    this._drawnGen++;
  }

  // a tile's collision goes (added or in flight: a late answer is dropped)
  _uncol(key) {
    if (this.collider) this.collider.removeTile(key);
    this.colLoaded.delete(key);
    this._colIn.delete(key);
  }

  // R8h2 world swap (worldswap.js buildingsHook): `rels` moved under the
  // running client — buildings/<tx>_<ty>.glb drops that tile's mesh,
  // buildings/col_<tx>_<ty>.bin its collision (a load in flight is dropped as
  // it lands: its bytes are the old world's), and the next update() runs a
  // ring pass, so the ring streams exactly those again. Every other tile stays
  // as it is. `tiles` = the new meta.buildings.tiles (the same tile set —
  // worldswap.js checked it —, their tris may have moved).
  invalidateTiles(rels, tiles = null) {
    if (tiles) this.info = tiles;
    for (const r of rels) {
      const g = /^buildings\/(-?\d+_-?\d+)\.glb$/.exec(r), c = /^buildings\/col_(-?\d+_-?\d+)\.bin$/.exec(r);
      if (g) {
        this._dead.delete(g[1]);
        const have = this.tiles.get(g[1]);
        if (have === 'loading') this._stale.add(g[1]);
        else if (have) this._unmesh(g[1], have);
      }
      if (c) this._uncol(c[1]);
    }
    this._replan = true;
  }

  // `focus` is the Focus (client/src/focus.js) or null/undefined at boot.
  update(pos, focus) {
    if (!this.info.length) return;
    // the cut set moved (stationzones.js setWorldCuts): every tile splits again
    if (this._cutGen !== cutGen()) {
      this._cutGen = cutGen();
      for (const root of this.tiles.values()) if (root !== 'loading' && root.userData.cutGen !== this._cutGen) this._cutSplit(root);
    }
    this._drip();
    const fcfg = (focus && focus.active && focus.buildings) || null;
    const gen = fcfg ? focus.gen : -1;
    // The ring keeps its 64 m move gate; the focus pass keys on `focus.gen`
    // instead — panning the scope must re-scan where standing still would
    // otherwise pin the ring pass shut.
    const ringPass = this._replan || pos.distanceTo(this.lastUpdate) >= 64;
    const focusPass = gen !== this.focusGen || !!fcfg !== this.focusOn;
    if (!ringPass && !focusPass) return;
    if (ringPass) { this.lastUpdate.copy(pos); this._replan = false; }
    this.focusGen = gen;
    this.focusOn = !!fcfg;
    this.focusLayer = fcfg ? focus.layer : 0;

    const half = this.tileM * 0.71;
    const n = this.info.length;
    const drawCull = this.cull * drawK();
    const keys = new Array(n), dist = new Array(n);
    for (let i = 0; i < n; i++) {
      const [tx, ty] = this.info[i].tile;
      const cx = (tx + 0.5) * this.tileM, cz = (ty + 0.5) * this.tileM;
      keys[i] = tx + '_' + ty;
      dist[i] = Math.hypot(cx - pos.x, cz - pos.z) - half;
    }

    // Wedge want-set: tiles the ring will not keep anyway, nearest first,
    // accumulated while the triangles fit `tri_budget` (farplan.js
    // glbFocusWant: the far-building gate replays the same rule)
    this.focusWant = glbFocusWant(this.info, this.tileM, dist, focus, fcfg, this.cull);

    // this pass's ring loads, queued (see the ctor) — a ring pass replaces
    // the GLB queue, collision is added to (a focus pass can want it too)
    const glb = ringPass ? [] : null;
    const col = [];
    const wantCol = (key, tx, ty, d) => {
      if (this.collider && !this.colLoaded.has(key)) col.push({ key, tx, ty, d });
    };
    for (let i = 0; i < n; i++) {
      const t = this.info[i];
      const [tx, ty] = t.tile;
      const key = keys[i], d = dist[i];
      const ringKeep = d < this.cull + 200;           // resident as far as the ring cares
      const wantFocus = this.focusWant.has(key) && !ringKeep;
      const have = this.tiles.get(key);
      if (!have) {
        if (ringPass && d < this.cull) {
          wantCol(key, tx, ty, d);
          if (!this._dead.has(key)) glb.push({ t, key, d });
        } else if (wantFocus && !this._dead.has(key)) {
          this._load(t, key);                         // no collision out here
        }
        continue;
      }
      if (have === 'loading') {                       // layer decided when it lands
        if (d < this.cull) wantCol(key, tx, ty, d);
        continue;
      }
      if (ringKeep) {
        // walked into the wedge tile: it becomes an ordinary ring tile again,
        // and collision arrives on approach (the load itself was too far out)
        if (have.userData.focus) this._setFocus(have, false);
        if (d < this.cull) wantCol(key, tx, ty, d);
        // the draw rung's cull: hidden beyond it (a tile still awaiting its
        // reveal frame keeps hidden; _drip reads the flag)
        const culled = d >= drawCull;
        if (!!have.userData.culled !== culled) {
          have.userData.culled = culled;
          if (!this._reveal.some((r) => r.root === have)) { have.visible = !culled; this._drawnGen++; }
        }
      } else if (wantFocus) {
        if (!have.userData.focus) this._setFocus(have, true);
      } else if (ringPass || have.userData.focus) {
        // ring tiles leave on the ring's cadence; focus-only tiles leave as
        // soon as the wedge drops them (or focus goes inactive at all)
        this._evict(key, have);
      }
    }
    const byD = (a, b) => a.d - b.d;
    if (glb) this._glbQ = glb.sort(byD);
    this._colQ = (ringPass ? col : this._colQ.concat(col)).sort(byD);
    this._pump();
  }
}
